import { buildSessionStringFromTypicalSchedule } from '../../services/TradingViewDatafeedService/util.js';
import type { LightweightChartModel } from './index.js';
import type { LightweightChartAction } from './state/actions.js';
import { lightweightChartReducer } from './state/reducer.js';
import type { ChartMode, ChartSession, LightweightChartState } from './state/types.js';
import { createInitialChartState } from './state/types.js';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { PeriodType } from '@thinkalpha/language-services';
import type { BarLength } from '@thinkalpha/ui-types/contracts/timeframe';
import { inject, injectable } from 'src/features/ioc/index.js';
import { quantWsClient } from 'src/lib/newQuant/index.js';
import type { LightweightChartBarDataService, OHLCVBar } from 'src/services/LightweightChartBarDataService/index.js';
import type { SecurityMasterService } from 'src/services/SecurityMasterService/index.js';
import type { ReactBindings } from 'src/types/bindings.js';
import type uPlot from 'uplot';

interface ResolvedSymbol {
    ticker: string;
    timezone: string;
    /** Regular trading-hours session string (`HHMM-HHMM`). */
    regularSession: string;
    /** Full extended-hours session string (`HHMM-HHMM`). */
    extendedSession: string;
}

@injectable()
export class LightweightChartModelImpl implements LightweightChartModel {
    readonly #state: Signal<LightweightChartState>;
    readonly #barDataService: LightweightChartBarDataService;
    readonly #securityMasterService: SecurityMasterService;
    readonly #log: ReactBindings['Logger'];
    readonly #disposableStack = new DisposableStack();

    /** Live realtime subscription disposer. Replaced on every reload. */
    #subscription: Disposable | undefined;
    /**
     * Monotonic counter incremented every time `loadAndSubscribe` runs. Async
     * resolution / fetch / subscribe callbacks compare against this to drop
     * stale results when symbol/interval/session has changed mid-flight.
     */
    #generation = 0;

    readonly #stateRO: ReadonlySignal<LightweightChartState>;
    readonly #symbolSignal: ReadonlySignal<string>;
    readonly #barLengthSignal: ReadonlySignal<BarLength>;
    readonly #sessionSignal: ReadonlySignal<ChartSession>;
    readonly #modeSignal: ReadonlySignal<ChartMode>;
    readonly #loadingSignal: ReadonlySignal<boolean>;
    readonly #errorSignal: ReadonlySignal<string | null>;
    readonly #historyLoadedSignal: ReadonlySignal<boolean>;
    readonly #historyLookbackDaysSignal: ReadonlySignal<number>;
    readonly #lineDataSignal: ReadonlySignal<uPlot.AlignedData>;
    readonly #ohlcDataSignal: ReadonlySignal<uPlot.AlignedData>;

    constructor(
        @inject('LightweightChartBarDataService') barDataService: LightweightChartBarDataService,
        @inject('SecurityMasterService') securityMasterService: SecurityMasterService,
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#barDataService = barDataService;
        this.#securityMasterService = securityMasterService;
        this.#log = logger.getSubLogger({ name: 'LightweightChartModel' });
        this.#state = signal(createInitialChartState());

        this.#stateRO = computed(() => this.#state.value);
        this.#symbolSignal = computed(() => this.#state.value.symbol);
        this.#barLengthSignal = computed(() => this.#state.value.barLength);
        this.#sessionSignal = computed(() => this.#state.value.session);
        this.#modeSignal = computed(() => this.#state.value.mode);
        this.#loadingSignal = computed(() => this.#state.value.loading);
        this.#errorSignal = computed(() => this.#state.value.error);
        this.#historyLoadedSignal = computed(() => this.#state.value.historyLoaded);
        this.#historyLookbackDaysSignal = computed(() => this.#state.value.historyLookbackDays);
        this.#lineDataSignal = computed(() => barsToLineData(this.#state.value.bars));
        this.#ohlcDataSignal = computed(() => barsToOhlcData(this.#state.value.bars));

        let lastSymbol = '';
        let lastBarLength: BarLength | undefined;
        let lastSession: ChartSession | undefined;
        let lastLookbackDays = 0;
        const reload = effect(() => {
            const symbol = this.#symbolSignal.value;
            const barLength = this.#barLengthSignal.value;
            const session = this.#sessionSignal.value;
            const lookbackDays = this.#historyLookbackDaysSignal.value;
            // Explicit dedup: only react to *real* changes in the reload-relevant
            // tuple. The computed signals can re-fire when the underlying state
            // object identity changes even if the projected field is equal, so
            // we guard here to avoid double-loading.
            if (
                symbol === lastSymbol &&
                barLength === lastBarLength &&
                session === lastSession &&
                lookbackDays === lastLookbackDays
            ) {
                return;
            }
            lastSymbol = symbol;
            lastBarLength = barLength;
            lastSession = session;
            lastLookbackDays = lookbackDays;
            if (!symbol) {
                // Clearing the symbol must also cancel any in-flight history
                // load + live realtime subscription kicked off for the prior
                // symbol. Bumping the generation invalidates pending awaits in
                // `#runLoad`; disposing the active subscription stops realtime
                // bars from landing post-clear and re-populating state. We
                // also flip `loading` back to false here because `#runLoad`'s
                // generation guard short-circuits the abandoned await before
                // it can land a HISTORY_LOAD_SUCCESS/ERROR, which would
                // otherwise leave the view stuck on its "Loading…" spinner.
                this.#generation++;
                if (this.#subscription) {
                    this.#subscription[Symbol.dispose]();
                    this.#subscription = undefined;
                }
                if (this.#state.value.loading) {
                    this.#state.value = { ...this.#state.value, loading: false };
                }
                return;
            }
            this.#loadAndSubscribe(symbol, barLength, session);
        });
        this.#disposableStack.defer(reload);
    }

    get state(): ReadonlySignal<LightweightChartState> {
        return this.#stateRO;
    }
    get symbol(): ReadonlySignal<string> {
        return this.#symbolSignal;
    }
    get barLength(): ReadonlySignal<BarLength> {
        return this.#barLengthSignal;
    }
    get session(): ReadonlySignal<ChartSession> {
        return this.#sessionSignal;
    }
    get mode(): ReadonlySignal<ChartMode> {
        return this.#modeSignal;
    }
    get loading(): ReadonlySignal<boolean> {
        return this.#loadingSignal;
    }
    get error(): ReadonlySignal<string | null> {
        return this.#errorSignal;
    }
    get historyLoaded(): ReadonlySignal<boolean> {
        return this.#historyLoadedSignal;
    }
    get historyLookbackDays(): ReadonlySignal<number> {
        return this.#historyLookbackDaysSignal;
    }
    get lineData(): ReadonlySignal<uPlot.AlignedData> {
        return this.#lineDataSignal;
    }
    get ohlcData(): ReadonlySignal<uPlot.AlignedData> {
        return this.#ohlcDataSignal;
    }

    dispatch(action: LightweightChartAction): void {
        const next = lightweightChartReducer(this.#state.value, action);
        if (next === this.#state.value) return;
        this.#state.value = next;
    }

    setSymbol(symbol: string): void {
        this.dispatch({ type: 'SET_SYMBOL', symbol });
    }
    setBarLength(barLength: BarLength): void {
        this.dispatch({ type: 'SET_BAR_LENGTH', barLength });
    }
    setSession(session: ChartSession): void {
        this.dispatch({ type: 'SET_SESSION', session });
    }
    setMode(mode: ChartMode): void {
        this.dispatch({ type: 'SET_MODE', mode });
    }
    setHistoryLookbackDays(days: number): void {
        this.dispatch({ type: 'SET_HISTORY_LOOKBACK_DAYS', days });
    }

    #loadAndSubscribe(symbol: string, barLength: BarLength, session: ChartSession): void {
        if (this.#subscription) {
            this.#subscription[Symbol.dispose]();
            this.#subscription = undefined;
        }
        const gen = ++this.#generation;
        this.dispatch({ type: 'HISTORY_LOAD_START' });
        void this.#runLoad(symbol, barLength, session, gen);
    }

    async #runLoad(symbol: string, barLength: BarLength, session: ChartSession, gen: number): Promise<void> {
        try {
            const resolved = await this.#resolveSymbol(symbol);
            if (gen !== this.#generation) return;

            const now = Math.floor(Date.now() / 1000);
            const lookbackDays = this.#state.value.historyLookbackDays;
            const from = now - lookbackDays * 86_400;
            const sessionWindow = pickSessionWindow(resolved, session);
            const fullMarketSession = resolved.extendedSession;

            // Bardex's `/queries/tv` requires `countBack > 0` and, in practice,
            // returns up to `countBack` bars regardless of the supplied
            // timeframe — so we must scale `countBack` to the lookback window
            // (with a generous 1.5× headroom for variance between calendar and
            // trading days) and cap at `maxBars`. Without this, a 90-day
            // lookback would still backfill ~4 years of daily bars.
            const countBack = Math.min(
                this.#state.value.maxBars,
                Math.max(1, Math.ceil(approxBarsPerDay(barLength, session) * lookbackDays * 1.5)),
            );

            const bars = await this.#barDataService.fetchHistory({
                ticker: symbol,
                barLengthBardex: barLengthToBardex(barLength),
                from,
                to: now,
                countBack,
                sessionId: session,
                sessionWindow,
                timezone: resolved.timezone,
            });
            if (gen !== this.#generation) return;

            this.dispatch({ type: 'HISTORY_LOAD_SUCCESS', bars });

            const sub = this.#barDataService.subscribeRealtime(
                {
                    ticker: symbol,
                    barLength,
                    extended: session === 'extended',
                    fullMarketSession,
                    timezone: resolved.timezone,
                },
                (bar) => {
                    if (gen !== this.#generation) return;
                    // Copy: the bar service reuses its mutable bar object across
                    // ticks and the reducer needs a stable snapshot.
                    this.dispatch({ type: 'REALTIME_BAR', bar: { ...bar } });
                },
            );
            if (gen !== this.#generation) {
                sub[Symbol.dispose]();
                return;
            }
            this.#subscription = sub;
        } catch (error) {
            if (gen !== this.#generation) return;
            const message = error instanceof Error ? error.message : String(error);
            this.#log.error('Failed to load chart history', {
                symbol,
                barLength,
                session,
                error: message,
                // Full error object so the Bardex response status / payload
                // surfaces in the console when debugging request shape issues.
                rawError: error,
            });
            this.dispatch({ type: 'HISTORY_LOAD_ERROR', error: message });
        }
    }

    async #resolveSymbol(symbol: string): Promise<ResolvedSymbol> {
        const entry = await this.#securityMasterService.fetchSecurityMasterEntry(symbol);
        const marketInfo = await quantWsClient.getMarketInfo(entry.PrimaryMIC);
        if (!marketInfo) {
            throw new Error(`Market data unavailable for ${symbol} (MIC ${entry.PrimaryMIC})`);
        }
        // Bardex (and TradingView) expect IANA `America/New_York`; some market
        // master records spell the same zone as `US/Eastern`. Normalize here
        // so the downstream request always uses the canonical form — TVDS does
        // the same conversion in its own `resolveSymbol`.
        const timezone = marketInfo.timezone === 'US/Eastern' ? 'America/New_York' : marketInfo.timezone;
        const regularEntries = marketInfo.typicalSchedule.filter((e) => e.status === 'Open');
        const allEntries = marketInfo.typicalSchedule.filter((e) => e.status !== 'Closed');
        const regularSession = buildSessionStringFromTypicalSchedule(regularEntries);
        const extendedSession = buildSessionStringFromTypicalSchedule(allEntries);
        return {
            ticker: symbol,
            timezone,
            regularSession,
            extendedSession,
        };
    }

    [Symbol.dispose](): void {
        // Bump generation so any pending async resolutions discard their work.
        this.#generation++;
        if (this.#subscription) {
            this.#subscription[Symbol.dispose]();
            this.#subscription = undefined;
        }
        this.#disposableStack[Symbol.dispose]();
    }
}

function barLengthToBardex(barLength: BarLength): string {
    switch (barLength.period) {
        case PeriodType.second:
            return `${barLength.value}s`;
        case PeriodType.minute:
            return `${barLength.value}m`;
        case PeriodType.hour:
            return `${barLength.value}h`;
        case PeriodType.day:
            return `${barLength.value}d`;
        case PeriodType.week:
            return `${barLength.value}w`;
        case PeriodType.month:
            // Bardex uses uppercase `M` for month; `m` is minute.
            return `${barLength.value}M`;
        case PeriodType.year:
            return `${barLength.value}y`;
        default:
            throw new Error(`Unsupported bar length period: ${String(barLength.period)}`);
    }
}

function pickSessionWindow(resolved: ResolvedSymbol, session: ChartSession): string {
    return session === 'extended' ? resolved.extendedSession : resolved.regularSession;
}

/**
 * Approximate bars per calendar day for the given bar length and session.
 * Used only as a sizing hint for the Bardex `countBack` cap so the response
 * fits inside the caller's lookback window. The factor is intentionally
 * conservative — the caller multiplies by a headroom factor to absorb
 * week-of-month variance. Sub-daily counts scale with the session window:
 * regular session is the standard 6.5h trading day (390 minutes); extended
 * spans pre-market 04:00 + regular + after-hours through 20:00 ET ≈ 16h
 * (960 minutes). Without the session split, intraday extended-session
 * lookbacks would request an undersized `countBack` and truncate.
 */
function approxBarsPerDay(barLength: BarLength, session: ChartSession): number {
    const tradingMinutesPerDay = session === 'extended' ? 960 : 390;
    switch (barLength.period) {
        case PeriodType.second:
            return (tradingMinutesPerDay * 60) / barLength.value;
        case PeriodType.minute:
            return tradingMinutesPerDay / barLength.value;
        case PeriodType.hour:
            return tradingMinutesPerDay / 60 / barLength.value;
        case PeriodType.day:
            return 1 / barLength.value;
        case PeriodType.week:
            // ~5 trading days per calendar week.
            return 1 / (barLength.value * 5);
        case PeriodType.month:
            // ~21 trading days per calendar month.
            return 1 / (barLength.value * 21);
        case PeriodType.year:
            // ~252 trading days per calendar year.
            return 1 / (barLength.value * 252);
        default:
            return 1;
    }
}

function barsToLineData(bars: readonly OHLCVBar[]): uPlot.AlignedData {
    const len = bars.length;
    const x = new Array<number>(len);
    const y = new Array<number>(len);
    for (let i = 0; i < len; i++) {
        // uPlot expects x in seconds for time series; bar.time is ms.
        x[i] = bars[i].time / 1000;
        y[i] = bars[i].close;
    }
    return [x, y];
}

function barsToOhlcData(bars: readonly OHLCVBar[]): uPlot.AlignedData {
    const len = bars.length;
    const x = new Array<number>(len);
    const open = new Array<number>(len);
    const high = new Array<number>(len);
    const low = new Array<number>(len);
    const close = new Array<number>(len);
    const volume = new Array<number>(len);
    for (let i = 0; i < len; i++) {
        x[i] = bars[i].time / 1000;
        open[i] = bars[i].open;
        high[i] = bars[i].high;
        low[i] = bars[i].low;
        close[i] = bars[i].close;
        volume[i] = bars[i].volume;
    }
    return [x, open, high, low, close, volume];
}
